import React, { FC, ReactNode, useEffect, useMemo } from 'react'

import Page from 'src/components/ui/atoms/Page'
import AdomikLoader from 'src/components/ui/atoms/PlaceHolder/AdomikLoader'
import PageHeader from 'src/components/ui/molecules/PageHeader'
import useAccount from 'src/hooks/useAccount'
import { adomikLoadingMessageGenerateDispatcher } from 'src/redux/actions/adomikLoadingActions'
import { useAppDispatch, useAppSelector } from 'src/redux/hooks'
import ComponentColor from 'src/types/ItemColor'
import { Product } from 'src/utils/product'
import { getDashboardKeyFn } from 'src/utils/saveDashboardUtils'

import * as styles from './ProductPageProvider.scss'

export interface ProductPageAPI {
  componentAvailability?: string
  componentData?: string
  entitiesSearch?: string
  translate?: string
}

export interface ProductPage {
  product: Product
  name: string
  code: string
  api: ProductPageAPI
  header?: any
  pageTitle?: string
  program?: string
  dashboardKey: string
  subProductTitle?: string
  componentColor: ComponentColor
}

export type ProductPageCtx = ProductPage

export const ProductPageContext = React.createContext<ProductPageCtx>({} as ProductPageCtx)

interface ProductPageProviderProps
  extends Omit<ProductPageCtx, 'api' | 'dashboardKey' | 'componentColor'> {
  children: ReactNode
  componentsSettingsLoading?: boolean // From redux store
  api?: ProductPageAPI
  componentColor?: ComponentColor
}

const ProductPageProvider: FC<ProductPageProviderProps> = ({
  children,
  product,
  name,
  code,
  api = {},
  header,
  program,
  subProductTitle,
  pageTitle,
  componentColor,
}) => {
  const { loadingSeller } = useAccount()
  const dispatch = useAppDispatch()
  const componentsSettingsLoading = useAppSelector(state => state.componentsSettings.loading)

  const contextValue = useMemo(
    () => ({
      product,
      name,
      code,
      api: {
        ...api,
        componentAvailability:
          api.componentAvailability || `/api/${product}/availability/component`, // DataDownload only
        componentData: api.componentData || `/api/${product}/component_v2`,
        entitiesSearch: api.entitiesSearch || `/api/${product}/entities_search`,
        translate: api.translate || `/api/${product}/entities/translate`,
      },
      dashboardKey: getDashboardKeyFn({ subProductCode: code, programId: program }),
      program: program?.toString(),
      subProductTitle,
      componentColor: componentColor || ComponentColor.blue,
    }),
    [product, name, code, api, program, subProductTitle, componentColor],
  )

  const isLoading = loadingSeller || componentsSettingsLoading

  // Generate a random message for the AdomikLoader
  useEffect(() => {
    if (!isLoading) {
      dispatch(adomikLoadingMessageGenerateDispatcher())
    }
  }, [dispatch, isLoading])

  return (
    <Page title={pageTitle}>
      <ProductPageContext.Provider value={contextValue}>
        <PageHeader
          componentsCodes={header?.componentsCodes}
          printModeAllowed={header?.print_mode_allowed}
          title={pageTitle}
        />
        {isLoading ? <AdomikLoader className={styles.AdomikLoader} /> : children}
      </ProductPageContext.Provider>
    </Page>
  )
}

export default ProductPageProvider
